import React, { useEffect } from 'react'

import { GetYearsOptions, Year } from 'api/vehicles/getYears'
import { FormikDropdown } from 'components/FormikFields'
import { FormikDropdownChild } from 'components/FormikFields/FormikDropdown/FormikDropdown'
import { setFieldValueAndTouched } from 'form/fields/utils/fieldUtils'
import useVehicleYears from 'hooks/useVehicleYears'

interface queryProps {
    lenderId?: number
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    formik: any
    dataSource?: string[]
    assetType?: unknown
    vehicleNumber: number
    prefilled?: boolean
    vehicleTypeList?: string[]
    excludeVehicleTypesList?: string[]
    captive?: string
}

const defaultProps = {
    lenderId: null,
    dataSource: null,
    assetType: null,
    prefilled: false,
    vehicleTypeList: null,
    excludeVehicleTypesList: null,
    captive: undefined,
}

export default function YearDropdown({
    lenderId,
    formik,
    dataSource,
    assetType,
    vehicleNumber,
    prefilled,
    vehicleTypeList,
    excludeVehicleTypesList,
    captive,
}: queryProps) {
    const { vehicle } = formik.values
    const { condition } = vehicle
    const FIRST_VEHICLE = 1
    const IS_FIRST_VEHICLE = vehicleNumber === FIRST_VEHICLE

    const vehicleFieldName = IS_FIRST_VEHICLE
        ? 'vehicle.primary.year'
        : 'vehicle.secondary.year'

    const year = IS_FIRST_VEHICLE
        ? vehicle.primary.year
        : vehicle.secondary.year

    let options: GetYearsOptions | null = null

    if (condition && !prefilled) {
        options = {
            condition,
            lenderId,
            dataSource,
            assetType,
            vehicleTypeList,
            excludeVehicleTypesList,
            captive,
        } as GetYearsOptions
    }

    const { data: years, error, isValidating } = useVehicleYears(options)
    const response = years ?? []
    const { setFieldValue, setFieldTouched } = formik

    useEffect(() => {
        if (!years || error || isValidating || prefilled) {
            return
        }

        if (!years.some((option) => `${option.id}` === year)) {
            setFieldValue(vehicleFieldName, '')

            if (IS_FIRST_VEHICLE) {
                setFieldValue('vehicle.makeId', '')
                setFieldValue('vehicle.primary.modelId', '')
                setFieldValue('vehicle.primary.selectedVehicleId', '')
                setFieldValue('vehicle.vehicleType', '')
            } else {
                setFieldValue('vehicle.secondary.modelId', '')
                setFieldValue('vehicle.secondary.selectedVehicleId', '')
            }
        }

        if (years.length === 1) {
            const onlyYear = years[0].id.toString()

            if (year === onlyYear) {
                setFieldTouched(vehicleFieldName, true)
            } else {
                setFieldValueAndTouched(
                    { setFieldValue, setFieldTouched },
                    vehicleFieldName,
                    onlyYear,
                )
            }
        }
    }, [
        years,
        error,
        isValidating,
        prefilled,
        year,
        vehicleFieldName,
        IS_FIRST_VEHICLE,
        setFieldValue,
        setFieldTouched,
    ])

    if (response.length < 1 && !prefilled) {
        return (
            <FormikDropdown
                name={
                    IS_FIRST_VEHICLE
                        ? 'vehicle.primary.year'
                        : 'vehicle.secondary.year'
                }
                label="Year"
                placeholder="Select Year"
                disabled
            >
                {{ name: '', value: '' }}
                {{ name: '', value: '' }}
            </FormikDropdown>
        )
    }

    if (prefilled) {
        return (
            <FormikDropdown
                name={
                    IS_FIRST_VEHICLE
                        ? 'vehicle.primary.year'
                        : 'vehicle.secondary.year'
                }
                label="Year"
                placeholder=""
                prefilled={prefilled}
            >
                {{
                    name: vehicle.primary.year,
                    value: vehicle.primary.year,
                }}
                {{
                    name: vehicle.secondary?.year,
                    value: vehicle.secondary?.year,
                }}
            </FormikDropdown>
        )
    }

    return (
        <FormikDropdown
            name={
                IS_FIRST_VEHICLE
                    ? 'vehicle.primary.year'
                    : 'vehicle.secondary.year'
            }
            label="Year"
            placeholder="Select Year"
            disabled={response.length === 0}
            prefilled={prefilled}
            error={year === null || year === ''}
            searchable
            searchPlaceholder="Search years..."
        >
            {response.map((child: Year) => {
                const newBorn: FormikDropdownChild = {
                    name: child.id.toString(),
                    value: child.id.toString(),
                }

                return newBorn
            })}
        </FormikDropdown>
    )
}

YearDropdown.defaultProps = defaultProps
